/* eslint-disable react/no-array-index-key */
import React from 'react';

import {
  Heading,
  Paragraph,
  List,
  ListItem,
  Link,
  Container,
  TableRow,
  Text,
} from '@constellation/core';
import { useContent } from '@interstellar/react-app-content';
import {
  StyledTable,
  StyledTableCol,
} from 'client/components/basicTable/BasicTable.styled';
import { TaggedAccordion } from 'client/components/taggedAccordion/TaggedAccordion';
import testIds from 'client/testIds';

import { LimitedCompanyCriteriaContent } from '../LimitedCompanyCriteria.config';
import BackToTopLink from './BackToTopLink';

function LendingSection() {
  const { lending, backToTopLabel, jumpToASection } =
    useContent<LimitedCompanyCriteriaContent>();

  const {
    assignableContracts,
    bankruptcies,
    businessConsumerBTL,
    concessionaryPurchase,
    countyCourtJudgements,
    creditScoreDeclineAppeals,
    dependants,
    deposits,
    firstTimeBuyerFirstTimeLandlord,
    lettingCriteria,
    lendingLimits,
    minMaxAge,
    minMaxTerm,
    mortgageArrears,
    mortgageFreeUnencumberedProperties,
    notionalStressRates,
    portfolioLandlords,
    remortgageAndCapitalRaising,
    repaymentMethods,
    id,
    title,
  } = lending;

  return (
    <section
      id={id}
      data-testid={testIds.limitedCompanyCriteria.lendingSection}
    >
      <Heading
        size="s5"
        marginBottom="05"
        marginTop="05"
        as="h2"
        dataTestId={testIds.limitedCompanyCriteria.lendingHeading}
      >
        {title}
      </Heading>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {assignableContracts.title}
          </Heading>
        }
        id={assignableContracts.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.assignableContracts}
      >
        {assignableContracts.paragraphs.map((item, idx) => (
          <Paragraph key={`assignable-contracts-${idx}`}>{item}</Paragraph>
        ))}
        <Heading as="h4" size="s2" marginTop="04">
          {assignableContracts.additionalInfo.title}
        </Heading>
        {assignableContracts.additionalInfo.links.map((link, idx) => (
          <Paragraph marginBottom="02" key={`assignable-contracts-link-${idx}`}>
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {bankruptcies.title}
          </Heading>
        }
        id={bankruptcies.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.bankruptcies}
      >
        <Paragraph>{bankruptcies.paragraph1}</Paragraph>
        <List marginBottom="05">
          <ListItem key="bankruptcies-l-1">{bankruptcies.listItem1}</ListItem>
        </List>
        <Paragraph>{bankruptcies.paragraph2}</Paragraph>
        <List marginBottom="05">
          <ListItem key="bankruptcies-l-2">{bankruptcies.listItem2}</ListItem>
        </List>
        <Paragraph>{bankruptcies.paragraph3}</Paragraph>

        <Heading as="h4" size="s2" marginTop="04">
          {bankruptcies.additionalInfo.title}
        </Heading>
        {bankruptcies.additionalInfo.links.map((link, idx) => (
          <Paragraph marginBottom="02" key={`bankruptcies-link-${idx}`}>
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {businessConsumerBTL.title}
          </Heading>
        }
        id={businessConsumerBTL.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.businessConsumerBTL}
      >
        <Paragraph>{businessConsumerBTL.paragraph1}</Paragraph>
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {concessionaryPurchase.title}
          </Heading>
        }
        id={concessionaryPurchase.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.concessionaryPurchase}
      >
        {concessionaryPurchase.paragraphs.map((item, idx) => (
          <Paragraph key={`concessionary-purchase-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {concessionaryPurchase.additionalInfo.title}
        </Heading>
        {concessionaryPurchase.additionalInfo.links.map((link, idx) => (
          <Paragraph
            marginBottom="02"
            key={`concessionary-purchase-link-${idx}`}
          >
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {countyCourtJudgements.title}
          </Heading>
        }
        id={countyCourtJudgements.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.countyCourtJudgements}
      >
        {countyCourtJudgements.paragraphs.map((item, idx) => (
          <Paragraph key={`county-court-judgements-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {countyCourtJudgements.additionalInfo.title}
        </Heading>
        {countyCourtJudgements.additionalInfo.links.map((link, idx) => (
          <Paragraph
            marginBottom="02"
            key={`county-court-judgements-link-${idx}`}
          >
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {creditScoreDeclineAppeals.title}
          </Heading>
        }
        id={creditScoreDeclineAppeals.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.creditScoreDeclineAppeals}
      >
        {creditScoreDeclineAppeals.paragraphs.map((item, idx) => (
          <Paragraph key={`decline-appeals-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {
            creditScoreDeclineAppeals
              .portingApplicationWithNoAdditionalBorrowing.title
          }
        </Heading>

        <Heading as="h5" size="s2" marginTop="02">
          {
            creditScoreDeclineAppeals
              .portingApplicationWithNoAdditionalBorrowing.bfpoAddress.title
          }
        </Heading>
        {creditScoreDeclineAppeals.portingApplicationWithNoAdditionalBorrowing.bfpoAddress.paragraphs.map(
          (item, idx) => (
            <Paragraph key={`bfpo-address-${idx}`}>{item}</Paragraph>
          ),
        )}

        <Heading as="h5" size="s2" marginTop="02">
          {
            creditScoreDeclineAppeals
              .portingApplicationWithNoAdditionalBorrowing.victimOfFraud.title
          }
        </Heading>
        {creditScoreDeclineAppeals.portingApplicationWithNoAdditionalBorrowing.victimOfFraud.paragraphs.map(
          (item, idx) => (
            <Paragraph key={`victim-of-fraud-${idx}`}>{item}</Paragraph>
          ),
        )}

        <Heading as="h4" size="s2" marginTop="04">
          {creditScoreDeclineAppeals.adverseCreditDataRegisteredInError.title}
        </Heading>

        <Heading as="h5" size="s2" marginTop="02">
          {
            creditScoreDeclineAppeals.adverseCreditDataRegisteredInError
              .anErrorByLBG.title
          }
        </Heading>
        {creditScoreDeclineAppeals.adverseCreditDataRegisteredInError.anErrorByLBG.paragraphs.map(
          (item, idx) => (
            <Paragraph key={`appeals-an-error-by-lbg-${idx}`}>{item}</Paragraph>
          ),
        )}
        <Paragraph>
          <Link
            href={
              creditScoreDeclineAppeals.adverseCreditDataRegisteredInError
                .anErrorByLBG.link.url
            }
          >
            {
              creditScoreDeclineAppeals.adverseCreditDataRegisteredInError
                .anErrorByLBG.link.label
            }
          </Link>
        </Paragraph>
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {dependants.title}
          </Heading>
        }
        id={dependants.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.dependants}
      >
        {dependants.paragraphs.map((item, idx) => (
          <Paragraph key={`dependants-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {dependants.childDependants.title}
        </Heading>
        {dependants.childDependants.paragraphs.map((item, idx) => (
          <Paragraph key={`dependants-child-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {dependants.adultDependant.title}
        </Heading>
        {dependants.adultDependant.paragraphs.map((item, idx) => (
          <Paragraph key={`dependants-adult-${idx}`}>{item}</Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {deposits.title}
          </Heading>
        }
        id={deposits.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.deposits}
      >
        {deposits.paragraphs.map((item, idx) => (
          <Paragraph key={`deposits-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {deposits.weCantAcceptDepositSources.title}
        </Heading>
        <List marginBottom="05">
          {deposits.weCantAcceptDepositSources.listItems.map((item, idx) => (
            <ListItem key={`deposits-cantaccept-${idx}`}>{item}</ListItem>
          ))}
        </List>

        <Heading as="h4" size="s2" marginTop="04">
          {deposits.weCanAcceptDepositSources.title}
        </Heading>

        <Heading as="h5" size="s2" marginTop="02">
          {deposits.weCanAcceptDepositSources.directorLoans.title}
        </Heading>

        <Heading
          as="h5"
          size="s2"
          marginTop="02"
          id={deposits.weCanAcceptDepositSources.equity.id}
        >
          {deposits.weCanAcceptDepositSources.equity.title}
        </Heading>
        <List marginBottom="05">
          {deposits.weCanAcceptDepositSources.equity.listItems.map(
            (item, idx) => (
              <ListItem key={`deposits-equity-${idx}`}>{item}</ListItem>
            ),
          )}
        </List>
        <Paragraph>
          {deposits.weCanAcceptDepositSources.equity.paragraph}
        </Paragraph>

        <Heading as="h5" size="s2" marginTop="02">
          {deposits.weCanAcceptDepositSources.gift.title}
        </Heading>
        {deposits.weCanAcceptDepositSources.gift.paragraphs1.map(
          (item, idx) => (
            <Paragraph key={`deposits-gift-p1-${idx}`}>{item}</Paragraph>
          ),
        )}
        <Paragraph weight="bold">
          {deposits.weCanAcceptDepositSources.gift.boldParagraph}
        </Paragraph>

        <Heading as="h6" size="s2" marginTop="02">
          {deposits.weCanAcceptDepositSources.gift.itMustInclude.title}
        </Heading>
        <List marginBottom="05">
          {deposits.weCanAcceptDepositSources.gift.itMustInclude.listItems.map(
            (item, idx) => (
              <ListItem key={`deposits-gift-include-${idx}`}>{item}</ListItem>
            ),
          )}
        </List>

        <Heading as="h6" size="s2" marginTop="02">
          {deposits.weCanAcceptDepositSources.gift.itMustConfirm.title}
        </Heading>
        <List marginBottom="05">
          {deposits.weCanAcceptDepositSources.gift.itMustConfirm.listItems.map(
            (item, idx) => (
              <ListItem key={`deposits-gift-confirm-${idx}`}>{item}</ListItem>
            ),
          )}
        </List>
        {deposits.weCanAcceptDepositSources.gift.paragraphs2.map(
          (item, idx) => (
            <Paragraph key={`deposits-gift-p2-${idx}`}>{item}</Paragraph>
          ),
        )}
        <List marginBottom="05">
          {deposits.weCanAcceptDepositSources.gift.listItems.map(
            (item, idx) => (
              <ListItem key={`deposits-gift-li-${idx}`}>{item}</ListItem>
            ),
          )}
        </List>

        <Container padding="none" marginBottom="05">
          <StyledTable
            headings={deposits.weCanAcceptDepositSources.gift.table.headings}
            dataTestId="standard-idv-documents-table"
          >
            {deposits.weCanAcceptDepositSources.gift.table.rows.map(
              (row, rowIdx) => (
                <TableRow key={`idv-table-${rowIdx}`}>
                  {row.map((el, colIdx) => (
                    <StyledTableCol
                      key={`idv-table-${rowIdx}-${colIdx}`}
                      data-testid={`idv-table-${rowIdx}-${colIdx}`}
                      heading={
                        deposits.weCanAcceptDepositSources.gift.table.headings[
                          colIdx
                        ]
                      }
                    >
                      <Text>{el}</Text>
                    </StyledTableCol>
                  ))}
                </TableRow>
              ),
            )}
          </StyledTable>
        </Container>

        <Heading as="h5" size="s2" marginTop="02">
          {deposits.weCanAcceptDepositSources.inheritance.title}
        </Heading>
        <Paragraph>
          {deposits.weCanAcceptDepositSources.inheritance.paragraph}
        </Paragraph>
        <Paragraph>
          <Link href={deposits.weCanAcceptDepositSources.inheritance.link.url}>
            {deposits.weCanAcceptDepositSources.inheritance.link.label}
          </Link>
        </Paragraph>

        <Heading as="h5" size="s2" marginTop="02">
          {deposits.weCanAcceptDepositSources.previousPropertySale.title}
        </Heading>
        <Paragraph>
          {deposits.weCanAcceptDepositSources.previousPropertySale.paragraph}
        </Paragraph>

        <Heading
          as="h5"
          size="s2"
          marginTop="02"
          id={deposits.weCanAcceptDepositSources.ukSavings.id}
        >
          {deposits.weCanAcceptDepositSources.ukSavings.title}
        </Heading>
        {deposits.weCanAcceptDepositSources.ukSavings.paragraphs.map(
          (item, idx) => (
            <Paragraph key={`deposits-uksavings-${idx}`}>{item}</Paragraph>
          ),
        )}

        <Heading as="h5" size="s2" marginTop="02">
          {deposits.weCanAcceptDepositSources.shares.title}
        </Heading>
        <Paragraph>
          {deposits.weCanAcceptDepositSources.shares.paragraph}
        </Paragraph>
        <List marginBottom="05">
          {deposits.weCanAcceptDepositSources.shares.listItems.map(
            (item, idx) => (
              <ListItem key={`deposits-shares-li-${idx}`}>{item}</ListItem>
            ),
          )}
        </List>

        <Heading as="h5" size="s2" marginTop="02">
          {deposits.weCanAcceptDepositSources.premiumBonds.title}
        </Heading>

        <Heading as="h6" size="s2" marginTop="02">
          {
            deposits.weCanAcceptDepositSources.premiumBonds.youllNeedToProvide
              .title
          }
        </Heading>
        <List marginBottom="05">
          {deposits.weCanAcceptDepositSources.premiumBonds.youllNeedToProvide.listItems.map(
            (item, idx) => (
              <ListItem key={`deposits-premiumbonds-li-${idx}`}>
                {item}
              </ListItem>
            ),
          )}
        </List>

        <Heading as="h5" size="s2" marginTop="02">
          {deposits.weCanAcceptDepositSources.passbooks.title}
        </Heading>
        <List marginBottom="05">
          {deposits.weCanAcceptDepositSources.passbooks.listItems.map(
            (item, idx) => (
              <ListItem key={`deposits-passbooks-li-${idx}`}>{item}</ListItem>
            ),
          )}
        </List>
        <Paragraph>
          {deposits.weCanAcceptDepositSources.passbooks.paragraph}
        </Paragraph>

        <Heading as="h5" size="s2" marginTop="02">
          {deposits.weCanAcceptDepositSources.additionalInfo.title}
        </Heading>
        {deposits.weCanAcceptDepositSources.additionalInfo.links.map(
          (link, idx) => (
            <Paragraph
              marginBottom="02"
              key={`deposits-additionalinfo-link-${idx}`}
            >
              <Link href={link.url}>{link.label}</Link>
            </Paragraph>
          ),
        )}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {firstTimeBuyerFirstTimeLandlord.title}
          </Heading>
        }
        id={firstTimeBuyerFirstTimeLandlord.id}
        variation="inpage1"
        dataTestId={
          testIds.limitedCompanyCriteria.firstTimeBuyerFirstTimeLandlord
        }
      >
        <Paragraph>{firstTimeBuyerFirstTimeLandlord.paragraph}</Paragraph>

        <Heading as="h4" size="s2" marginTop="04">
          {firstTimeBuyerFirstTimeLandlord.moreThanOneDirector.title}
        </Heading>
        {firstTimeBuyerFirstTimeLandlord.moreThanOneDirector.paragraphs.map(
          (item, idx) => (
            <Paragraph key={`first-time-landlord-director-${idx}`}>
              {item}
            </Paragraph>
          ),
        )}

        <Heading as="h4" size="s2" marginTop="04">
          {firstTimeBuyerFirstTimeLandlord.acceptableDocuments.title}
        </Heading>

        <Heading as="h5" size="s2" marginTop="02">
          {
            firstTimeBuyerFirstTimeLandlord.acceptableDocuments.englandAndWales
              .title
          }
        </Heading>
        <List marginBottom="05">
          {firstTimeBuyerFirstTimeLandlord.acceptableDocuments.englandAndWales.listItems.map(
            (item, idx) => (
              <ListItem key={`first-time-landlord-ew-${idx}`}>{item}</ListItem>
            ),
          )}
        </List>

        <Heading as="h5" size="s2" marginTop="02">
          {
            firstTimeBuyerFirstTimeLandlord.acceptableDocuments
              .scotlandAndNorthernIreland.title
          }
        </Heading>
        <List>
          {firstTimeBuyerFirstTimeLandlord.acceptableDocuments.scotlandAndNorthernIreland.listItems.map(
            (item, idx) => (
              <ListItem key={`first-time-landlord-sni-${idx}`}>{item}</ListItem>
            ),
          )}
        </List>
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {lettingCriteria.title}
          </Heading>
        }
        id={lettingCriteria.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.lettingCriteria}
      >
        <Heading as="h4" size="s2" marginTop="04">
          {lettingCriteria.thePropertyCanBeLetOn.title}
        </Heading>
        <List marginBottom="05">
          {lettingCriteria.thePropertyCanBeLetOn.listItems.map((item, idx) => (
            <ListItem key={`property-can-be-let-on-list-${idx}`}>
              {item}
            </ListItem>
          ))}
        </List>
        {lettingCriteria.thePropertyCanBeLetOn.paragraphs.map((item, idx) => (
          <Paragraph key={`property-can-be-let-on-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {lettingCriteria.weCantAcceptTenants.title}
        </Heading>
        <List marginBottom="05">
          {lettingCriteria.weCantAcceptTenants.listItems1.map((item, idx) => (
            <ListItem key={`we-cant-accept-tenants-list-${idx}`}>
              {item}
            </ListItem>
          ))}
        </List>
        {lettingCriteria.weCantAcceptTenants.paragraphs.map((item, idx) => (
          <Paragraph key={`we-cant-accept-tenants-${idx}`}>{item}</Paragraph>
        ))}
        <List>
          {lettingCriteria.weCantAcceptTenants.listItems2.map((item, idx) => (
            <ListItem key={`we-cant-accept-tenants-list-2-${idx}`}>
              {item}
            </ListItem>
          ))}
        </List>

        <Heading as="h4" size="s2" marginTop="04">
          {lettingCriteria.additionalInfo.title}
        </Heading>
        <Paragraph>
          <Link href={lettingCriteria.additionalInfo.link.url}>
            {lettingCriteria.additionalInfo.link.label}
          </Link>
        </Paragraph>
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {lendingLimits.title}
          </Heading>
        }
        id={lendingLimits.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.lendingLimits}
      >
        {lendingLimits.paragraphs1.map((item, idx) => (
          <Paragraph key={`lending-limits-1-${idx}`}>{item}</Paragraph>
        ))}
        <List marginBottom="05">
          {lendingLimits.listItems.map((item, idx) => (
            <ListItem key={`lending-limits-${idx}`}>{item}</ListItem>
          ))}
        </List>
        {lendingLimits.paragraphs2.map((item, idx) => (
          <Paragraph key={`lending-limits-2-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {lendingLimits.additionalInfo.title}
        </Heading>
        {lendingLimits.additionalInfo.links.map((link, idx) => (
          <Paragraph marginBottom="02" key={`lending-limits-link-${idx}`}>
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {minMaxAge.title}
          </Heading>
        }
        id={minMaxAge.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.minMaxAge}
      >
        {minMaxAge.paragraphs.map((item, idx) => (
          <Paragraph key={`min-max-age-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {minMaxAge.additionalInfo.title}
        </Heading>
        {minMaxAge.additionalInfo.links.map((link, idx) => (
          <Paragraph marginBottom="02" key={`min-max-age-link-${idx}`}>
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {minMaxTerm.title}
          </Heading>
        }
        id={minMaxTerm.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.minMaxTerm}
      >
        {minMaxTerm.paragraphs.map((item, idx) => (
          <Paragraph key={`min-max-term-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {minMaxTerm.additionalInfo.title}
        </Heading>
        {minMaxTerm.additionalInfo.links.map((link, idx) => (
          <Paragraph marginBottom="02" key={`min-max-term-link-${idx}`}>
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {mortgageArrears.title}
          </Heading>
        }
        id={mortgageArrears.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.mortgageArrears}
      >
        <Paragraph>{mortgageArrears.paragraph}</Paragraph>

        <Heading as="h4" size="s2" marginTop="04">
          {mortgageArrears.additionalInfo.title}
        </Heading>
        {mortgageArrears.additionalInfo.links.map((link, idx) => (
          <Paragraph marginBottom="02" key={`mortgage-arrears-link-${idx}`}>
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {mortgageFreeUnencumberedProperties.title}
          </Heading>
        }
        id={mortgageFreeUnencumberedProperties.id}
        variation="inpage1"
        dataTestId={
          testIds.limitedCompanyCriteria.mortgageFreeUnencumberedProperties
        }
      >
        {mortgageFreeUnencumberedProperties.paragraphs.map((item, idx) => (
          <Paragraph key={`mortgage-free-unencumbered-properties-p-${idx}`}>
            {item}
          </Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {mortgageFreeUnencumberedProperties.additionalInfo.title}
        </Heading>
        {mortgageFreeUnencumberedProperties.additionalInfo.links.map(
          (link, idx) => (
            <Paragraph
              marginBottom="02"
              key={`mortgage-free-unencumbered-properties-link-${idx}`}
            >
              <Link href={link.url}>{link.label}</Link>
            </Paragraph>
          ),
        )}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {notionalStressRates.title}
          </Heading>
        }
        id={notionalStressRates.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.notionalStressRates}
      >
        <Paragraph>{notionalStressRates.paragraph}</Paragraph>

        <Heading as="h4" size="s2" marginTop="04">
          {notionalStressRates.additionalInfo.title}
        </Heading>
        {notionalStressRates.additionalInfo.links.map((link, idx) => (
          <Paragraph
            marginBottom="02"
            key={`notional-stress-rates-link-${idx}`}
          >
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {portfolioLandlords.title}
          </Heading>
        }
        id={portfolioLandlords.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.portfolioLandlords}
      >
        <Paragraph>{portfolioLandlords.paragraph}</Paragraph>

        <Heading as="h4" size="s2" marginTop="04">
          {portfolioLandlords.heresAnExample.title}
        </Heading>
        {portfolioLandlords.heresAnExample.paragraphs.map((item, idx) => (
          <Paragraph key={`portfolio-landlords-example-${idx}`}>
            {item}
          </Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {portfolioLandlords.ourProcess.title}
        </Heading>
        <List marginBottom="05">
          {portfolioLandlords.ourProcess.listItems.map((item, idx) => (
            <ListItem key={`portfolio-landlords-process-${idx}`}>
              {item}
            </ListItem>
          ))}
        </List>

        <Heading as="h4" size="s2" marginTop="04">
          {portfolioLandlords.informationNeededIncludes.title}
        </Heading>
        <List marginBottom="05">
          {portfolioLandlords.informationNeededIncludes.listItems.map(
            (item, idx) => (
              <ListItem key={`portfolio-landlords-needed-${idx}`}>
                {item}
              </ListItem>
            ),
          )}
        </List>
        <Paragraph weight="bold">
          {portfolioLandlords.informationNeededIncludes.boldParagraph}
        </Paragraph>

        <Heading as="h4" size="s2" marginTop="04">
          {portfolioLandlords.portfolioLandlordsCriteria.title}
        </Heading>
        <List>
          {portfolioLandlords.portfolioLandlordsCriteria.listItems.map(
            (item, idx) => (
              <ListItem key={`portfolio-landlords-criteria-${idx}`}>
                {item}
              </ListItem>
            ),
          )}
        </List>
        <Paragraph>
          {portfolioLandlords.portfolioLandlordsCriteria.paragraph}
        </Paragraph>

        <Heading as="h4" size="s2" marginTop="04">
          {portfolioLandlords.unencumberedLetProperties.title}
        </Heading>
        <Paragraph>
          {portfolioLandlords.unencumberedLetProperties.paragraph}
        </Paragraph>

        <Heading as="h4" size="s2" marginTop="04">
          {portfolioLandlords.financeCosts.title}
        </Heading>
        {portfolioLandlords.financeCosts.paragraphs.map((item, idx) => (
          <Paragraph key={`finance-costs-${idx}`}>{item}</Paragraph>
        ))}

        <Heading as="h4" size="s2" marginTop="04">
          {portfolioLandlords.portfolioLandlordHelpAndSupport.title}
        </Heading>
        {portfolioLandlords.portfolioLandlordHelpAndSupport.paragraphs.map(
          (item, idx) => (
            <Paragraph key={`pl-help-and-support-${idx}`}>{item}</Paragraph>
          ),
        )}

        <Heading as="h4" size="s2" marginTop="04">
          {portfolioLandlords.additionalInfo.title}
        </Heading>
        {portfolioLandlords.additionalInfo.links.map((link, idx) => (
          <Paragraph marginBottom="02" key={`pl-additional-info-${idx}`}>
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {remortgageAndCapitalRaising.title}
          </Heading>
        }
        id={remortgageAndCapitalRaising.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.remortgageAndCapitalRaising}
      >
        <Paragraph>{remortgageAndCapitalRaising.paragraph}</Paragraph>

        <Heading as="h4" size="s2" marginTop="04">
          {
            remortgageAndCapitalRaising.unacceptableReasonsForCapitalRaising
              .title
          }
        </Heading>
        <List marginBottom="05">
          {remortgageAndCapitalRaising.unacceptableReasonsForCapitalRaising.listItems.map(
            (item, idx) => (
              <ListItem
                key={`remortgage-and-capital-raising-unacceptable-${idx}`}
              >
                {item}
              </ListItem>
            ),
          )}
        </List>
        {remortgageAndCapitalRaising.unacceptableReasonsForCapitalRaising.paragraphs.map(
          (item, idx) => (
            <Paragraph
              key={`remortgage-and-capital-raising-unacceptable-${idx}`}
            >
              {item}
            </Paragraph>
          ),
        )}

        <Heading as="h4" size="s2" marginTop="04">
          {remortgageAndCapitalRaising.additionalInfo.title}
        </Heading>
        {remortgageAndCapitalRaising.additionalInfo.links.map((link, idx) => (
          <Paragraph
            marginBottom="02"
            key={`remortgage-and-capital-raising-link-${idx}`}
          >
            <Link href={link.url}>{link.label}</Link>
          </Paragraph>
        ))}
      </TaggedAccordion>

      <TaggedAccordion
        label={
          <Heading size="s3" as="h3">
            {repaymentMethods.title}
          </Heading>
        }
        id={repaymentMethods.id}
        variation="inpage1"
        dataTestId={testIds.limitedCompanyCriteria.repaymentMethods}
      >
        <Paragraph>{repaymentMethods.paragraph}</Paragraph>
      </TaggedAccordion>

      <BackToTopLink
        dataTestId={`${id}-back-to-top`}
        jumpToASectionId={jumpToASection.id}
      >
        {backToTopLabel}
      </BackToTopLink>
    </section>
  );
}

export default LendingSection;
